import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  BulkMembersRequest,
  CardOption,
  CreateFeeGroupRequest,
  EmergencyContactUpdateRequest,
  FeeDashboard,
  FeeDashboardPeriod,
  FeeGroupResponse,
  FeeMemberResponse,
  FeeMembershipResponse,
  GroupDetailResponse,
  IndustryItem,
  MemberCreateRequest,
  MemberPaymentPageResponse,
  MemberPersonalInfoUpdateRequest,
  MemberProfileResponse,
  RegisterToSubgroupRequest,
  SubgroupCreateRequest,
  SubgroupResponse,
  SubgroupUpdateRequest,
  UpdateEnrollmentDiscountRequest,
  UserIndustryResponse
} from '../interface/fee.interface';

const IMAGE_BASE = `${environment.apiUrl}/user/getImage`;

@Injectable({
  providedIn: 'root'
})
export class FeeService {

  private readonly apiUrl = `${environment.apiUrl}/fees`;

  constructor(private readonly http: HttpClient) {}

  getIndustries(): Observable<IndustryItem[]> {
    return this.http.get<IndustryItem[]>(`${this.apiUrl}/industries`);
  }

  getAvailableCards(excludeIndustryId?: number): Observable<CardOption[]> {
    let params = new HttpParams();
    if (excludeIndustryId != null) {
      params = params.set('excludeIndustryId', String(excludeIndustryId));
    }
    return this.http.get<CardOption[]>(`${this.apiUrl}/available-cards`, { params }).pipe(
      catchError(() => of([] as CardOption[]))
    );
  }

  hasUserIndustry(): Observable<boolean> {
    return this.http.get<UserIndustryResponse>(`${this.apiUrl}/group`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  getUserIndustry(): Observable<UserIndustryResponse | null> {
    return this.http.get<UserIndustryResponse>(`${this.apiUrl}/group`).pipe(
      catchError(() => of(null))
    );
  }

  getUserIndustries(): Observable<UserIndustryResponse[]> {
    return this.http.get<UserIndustryResponse[]>(`${this.apiUrl}/groups`).pipe(
      catchError(() => of([]))
    );
  }

  getUserIndustryById(id: number): Observable<UserIndustryResponse | null> {
    return this.http.get<UserIndustryResponse>(`${this.apiUrl}/groups/${id}`).pipe(
      catchError(() => of(null))
    );
  }

  deleteUserIndustry(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/groups/${id}`);
  }

  updateIndustryCard(industryId: number, cardId: number): Observable<UserIndustryResponse> {
    return this.http.put<UserIndustryResponse>(`${this.apiUrl}/groups/${industryId}/card`, null, {
      params: { userCardId: String(cardId) }
    });
  }

  createUserIndustry(fd: FormData): Observable<UserIndustryResponse> {
    return this.http.post<UserIndustryResponse>(this.apiUrl, fd);
  }

  getDashboard(period: FeeDashboardPeriod = 'THIS_WEEK', userIndustryId?: number): Observable<FeeDashboard> {
    let params = new HttpParams().set('period', period);
    if (userIndustryId != null) {
      params = params.set('userIndustryId', String(userIndustryId));
    }
    return this.http.get<FeeDashboard>(`${this.apiUrl}/dashboard`, { params });
  }

  getFeeGroups(industryId: number): Observable<FeeGroupResponse[]> {
    return this.http.get<FeeGroupResponse[]>(`${this.apiUrl}/${industryId}/groups`).pipe(
      map(groups => groups.map(group => this.withSortedSubgroups(group))),
      catchError(() => of([]))
    );
  }

  private withSortedSubgroups<T extends FeeGroupResponse>(group: T): T {
    return {
      ...group,
      subgroups: [...(group.subgroups ?? [])].sort((a, b) => a.id - b.id)
    };
  }

  createFeeGroup(industryId: number, request: CreateFeeGroupRequest): Observable<FeeGroupResponse> {
    return this.http.post<FeeGroupResponse>(`${this.apiUrl}/${industryId}/groups`, request);
  }

  getGroupDetail(groupId: number): Observable<GroupDetailResponse> {
    return this.http.get<GroupDetailResponse>(`${this.apiUrl}/group-detail/${groupId}`).pipe(
      map(group => this.withSortedSubgroups(group))
    );
  }

  updateGroupName(groupId: number, name: string): Observable<FeeGroupResponse> {
    return this.http.put<FeeGroupResponse>(`${this.apiUrl}/group-detail/${groupId}/name`, { name });
  }

  deleteGroup(groupId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/group-detail/${groupId}`);
  }

  createSubgroup(groupId: number, request: SubgroupCreateRequest): Observable<SubgroupResponse> {
    return this.http.post<SubgroupResponse>(`${this.apiUrl}/group-detail/${groupId}/subgroups`, request);
  }

  updateSubgroup(subgroupId: number, request: SubgroupUpdateRequest): Observable<SubgroupResponse> {
    return this.http.put<SubgroupResponse>(`${this.apiUrl}/subgroups/${subgroupId}`, request);
  }

  deleteSubgroup(subgroupId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/subgroups/${subgroupId}`);
  }

  bulkCreateMembers(request: BulkMembersRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/members/bulk`, request);
  }

  addMemberToSubgroup(subgroupId: number, member: Omit<MemberCreateRequest, 'subgroupId'>): Observable<void> {
    const request: BulkMembersRequest = {
      members: [{ ...member, subgroupId, frequency: member.frequency }]
    };
    return this.bulkCreateMembers(request);
  }

  getMembersBySubgroup(subgroupId: number): Observable<FeeMemberResponse[]> {
    return this.http.get<FeeMemberResponse[]>(`${environment.apiUrl}/v1/members/subgroups/${subgroupId}`).pipe(
      catchError(() => of([]))
    );
  }

  setMemberActive(memberId: number, active: boolean): Observable<void> {
    return this.http.patch<void>(`${environment.apiUrl}/v1/members/${memberId}/active`, null, {
      params: { active: String(active) }
    });
  }

  setEnrollmentActive(memberId: number, subgroupId: number, active: boolean): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/v1/members/${memberId}/subgroups/${subgroupId}/active`,
      null,
      { params: { active: String(active) } }
    );
  }

  getMemberProfile(memberId: number): Observable<MemberProfileResponse> {
    return this.http.get<MemberProfileResponse>(`${environment.apiUrl}/v1/members/${memberId}/profile`);
  }

  updateMemberPersonalInfo(memberId: number, request: MemberPersonalInfoUpdateRequest): Observable<MemberProfileResponse> {
    return this.http.patch<MemberProfileResponse>(`${environment.apiUrl}/v1/members/${memberId}/personal-info`, request);
  }

  updateEmergencyContact(memberId: number, request: EmergencyContactUpdateRequest): Observable<MemberProfileResponse> {
    return this.http.patch<MemberProfileResponse>(`${environment.apiUrl}/v1/members/${memberId}/personal-info`, request);
  }

  removeMemberFromSubgroup(memberId: number, subgroupId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/v1/members/${memberId}/subgroups/${subgroupId}`);
  }

  registerMemberToSubgroups(memberId: number, request: RegisterToSubgroupRequest): Observable<MemberProfileResponse> {
    return this.http.post<MemberProfileResponse>(`${environment.apiUrl}/v1/members/${memberId}/subgroups`, request);
  }

  updateEnrollmentDiscount(memberId: number, subgroupId: number, request: UpdateEnrollmentDiscountRequest): Observable<MemberProfileResponse> {
    return this.http.patch<MemberProfileResponse>(
      `${environment.apiUrl}/v1/members/${memberId}/subgroups/${subgroupId}/discount`,
      request
    );
  }

  sendMemberNotification(memberId: number, subgroupId?: number | null): Observable<void> {
    const params = subgroupId != null
      ? new HttpParams().set('subgroupId', String(subgroupId))
      : undefined;
    return this.http.post<void>(`${environment.apiUrl}/v1/members/${memberId}/notify`, null, { params });
  }

  generateMemberPaymentToken(memberId: number): Observable<string> {
    return this.http.get(`${environment.apiUrl}/v1/members/${memberId}/payment-token`, {
      responseType: 'text'
    });
  }

  getPaymentPage(token: string): Observable<MemberPaymentPageResponse> {
    return this.http.get<MemberPaymentPageResponse>(`${environment.apiUrl}/v1/members/public/payment/${token}`);
  }

  payRecord(token: string, recordId: number, amount?: number): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/v1/members/public/payment/${token}/records/${recordId}/pay`,
      amount != null ? { amount } : null
    );
  }

  markRecordPaidByAdmin(memberId: number, recordId: number): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}/v1/members/${memberId}/records/${recordId}/pay`,
      null
    );
  }

  cancelRecurringBinding(memberId: number, subgroupId: number): Observable<MemberProfileResponse> {
    return this.http.post<MemberProfileResponse>(
      `${environment.apiUrl}/v1/members/${memberId}/subgroups/${subgroupId}/recurring-binding/cancel`,
      null
    );
  }

  endBillingPeriod(memberId: number, recordId: number): Observable<MemberProfileResponse> {
    return this.http.post<MemberProfileResponse>(
      `${environment.apiUrl}/v1/members/${memberId}/records/${recordId}/end`,
      null
    );
  }

  getMyMemberships(): Observable<FeeMembershipResponse[]> {
    return this.http.get<FeeMembershipResponse[]>(`${environment.apiUrl}/v1/members/my-memberships`).pipe(
      catchError(() => of([]))
    );
  }

  generatePaymentQrCode(paymentUrl: string): Observable<Blob> {
    const url = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(paymentUrl)}`;
    return from(fetch(url).then(res => res.blob()));
  }

  getLogoUrl(logoPath: string | null | undefined): string | null {
    if (!logoPath) return null;
    return `${IMAGE_BASE}/${logoPath}`;
  }
}
